/**
 * ProviderLogos — small inline SVG marks for the LLM providers we route to.
 *
 * SVG path data is sourced from simple-icons (https://simpleicons.org/),
 * distributed under the MIT license. Trademarks remain with their owners;
 * using a brand's mark in a UI to identify the corresponding integration
 * is nominative use — the same pattern every connections page follows.
 *
 * Fills use ``currentColor`` so each logo inherits its container's text
 * colour. Set ``className="text-space"`` (etc.) on the wrapper to recolor.
 */

import type { ReactElement } from "react";

export type Provider = "claude" | "openrouter" | "openai" | "gemini" | "unknown";

/**
 * Derive the provider family from a stored ``messages.model`` value.
 *
 * Anthropic IDs start with ``claude-``; OpenRouter ids with ``openrouter/``.
 * The legacy ``openai-`` / ``gemini-`` prefixes are still recognised so
 * historical messages (sent before the OpenRouter migration) render their
 * provider mark. Anything else — including pre-v15 NULL rows — falls through
 * to ``unknown`` so the caller can render nothing instead of guessing.
 */
export function getProvider(model: string | null | undefined): Provider {
  if (!model) return "unknown";
  if (model.startsWith("claude-")) return "claude";
  if (model.startsWith("openrouter/")) return "openrouter";
  if (model.startsWith("openai-")) return "openai";
  if (model.startsWith("gemini-")) return "gemini";
  return "unknown";
}

export function AnthropicLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      role="img"
      aria-label="Anthropic"
    >
      <path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" />
    </svg>
  );
}

export function OpenAILogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      role="img"
      aria-label="OpenAI"
    >
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  );
}

export function GeminiLogo({ className }: { className?: string }) {
  // Google "Gemini" four-point star mark. Path data from simple-icons
  // (slug ``googlegemini``, MIT). ``currentColor`` lets us recolor it via
  // the wrapper className the same way the other provider marks do.
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      role="img"
      aria-label="Gemini"
    >
      <path d="M12 24A14.304 14.304 0 0 0 0 12 14.304 14.304 0 0 0 12 0a14.304 14.304 0 0 0 12 12 14.304 14.304 0 0 0-12 12" />
    </svg>
  );
}

export function OpenRouterLogo({ className }: { className?: string }) {
  // Official OpenRouter mark (24×24, evenodd fill).
  // ``currentColor`` lets the wrapper recolor it like the other marks.
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      fillRule="evenodd"
      role="img"
      aria-label="OpenRouter"
    >
      <path d="M16.804 1.957l7.22 4.105v.087L16.73 10.21l.017-2.117-.821-.03c-1.059-.028-1.611.002-2.268.11-1.064.175-2.038.577-3.147 1.352L8.345 11.03c-.284.195-.495.336-.68.455l-.515.322-.397.234.385.23.53.338c.476.314 1.17.796 2.701 1.866 1.11.775 2.083 1.177 3.147 1.352l.3.045c.694.091 1.375.094 2.825.033l.022-2.159 7.22 4.105v.087L16.589 22l.014-1.862-.635.022c-1.386.042-2.137.002-3.138-.162-1.694-.28-3.26-.926-4.881-2.059l-2.158-1.5a21.997 21.997 0 00-.755-.498l-.467-.28a55.927 55.927 0 00-.76-.43C2.908 14.73.563 14.116 0 14.116V9.888l.14.004c.564-.007 2.91-.622 3.809-1.124l1.016-.58.438-.274c.428-.28 1.072-.726 2.686-1.853 1.621-1.133 3.186-1.78 4.881-2.059 1.152-.19 1.974-.213 3.814-.138l.02-1.907z" />
    </svg>
  );
}

/**
 * Render the right provider mark for a given model id, or ``null`` for
 * unknown / NULL models so callers can use it directly in JSX without
 * a wrapper conditional.
 */
export function ProviderIcon({
  model,
  className = "w-4 h-4",
}: {
  model: string | null | undefined;
  className?: string;
}): ReactElement | null {
  const provider = getProvider(model);
  if (provider === "claude") return <AnthropicLogo className={className} />;
  if (provider === "openrouter") return <OpenRouterLogo className={className} />;
  if (provider === "openai") return <OpenAILogo className={className} />;
  if (provider === "gemini") return <GeminiLogo className={className} />;
  return null;
}
